import { z } from "zod";

import {
  buildStoredSession,
  SESSION_KEY,
  storeSession,
  type StoredSession,
} from "./session-storage";

// Converts sessions stored before the user lib owned them, so a deploy doesn't sign
// everyone out. Removable once those sessions have expired.

const LEGACY_AUTH_STATE_KEY = "authState";
const LEGACY_SSO_FLAG_KEY = "isAuth0";
const LEGACY_LOCAL_KEYS = [
  LEGACY_AUTH_STATE_KEY,
  "auth0State",
  LEGACY_SSO_FLAG_KEY,
];
const LEGACY_SESSION_KEYS = [LEGACY_AUTH_STATE_KEY, "user"];

const legacyAuthStateSchema = z.object({
  access_token: z.string().min(1),
  id_token: z.string().min(1).optional(),
  refresh_token: z.string().min(1).optional(),
});

/** The old storage values a migration reads; each is the raw stored string or null. */
export interface LegacyValues {
  readonly sessionStorageAuthState: string | null;
  readonly localStorageAuthState: string | null;
  readonly ssoFlag: string | null;
}

export interface LegacyMigration {
  readonly session?: StoredSession;
  /** A refresh token the old setup issued to a session that didn't ask to stay signed in. */
  readonly refreshTokenToRevoke?: string;
}

const parseLegacyAuthState = (raw: string | null) => {
  if (raw === null) return undefined;
  try {
    const parsed = legacyAuthStateSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
};

/**
 * The new session for an old one. The old code read sessionStorage first, so an
 * unchecked ("don't keep me signed in") login wins over a checked one.
 */
export const sessionFromLegacy = ({
  sessionStorageAuthState,
  localStorageAuthState,
  ssoFlag,
}: LegacyValues): LegacyMigration => {
  const unchecked = parseLegacyAuthState(sessionStorageAuthState);
  if (unchecked) {
    return {
      session: buildStoredSession(
        { accessToken: unchecked.access_token, idToken: unchecked.id_token },
        { keepSignedIn: false, provider: "password" },
      ),
      refreshTokenToRevoke: unchecked.refresh_token,
    };
  }

  const checked = parseLegacyAuthState(localStorageAuthState);
  if (!checked) return {};

  return {
    session: buildStoredSession(
      {
        accessToken: checked.access_token,
        idToken: checked.id_token,
        refreshToken: checked.refresh_token,
      },
      { keepSignedIn: true, provider: ssoFlag === "true" ? "sso" : "password" },
    ),
  };
};

/**
 * Moves an old-format session into the user lib's storage, once. The old keys are
 * cleared every time, so a stale copy in another tab can't be migrated after a logout.
 */
export const migrateLegacySession = (): LegacyMigration => {
  const migration =
    window.localStorage.getItem(SESSION_KEY) === null
      ? sessionFromLegacy({
          sessionStorageAuthState: window.sessionStorage.getItem(
            LEGACY_AUTH_STATE_KEY,
          ),
          localStorageAuthState: window.localStorage.getItem(
            LEGACY_AUTH_STATE_KEY,
          ),
          ssoFlag: window.localStorage.getItem(LEGACY_SSO_FLAG_KEY),
        })
      : {};
  LEGACY_LOCAL_KEYS.forEach((key) => window.localStorage.removeItem(key));
  LEGACY_SESSION_KEYS.forEach((key) => window.sessionStorage.removeItem(key));
  if (migration.session) storeSession(migration.session);

  return migration;
};
